/**
 * MietwagenWorkflowStepper — Visueller Prozessfortschritt für Mietwagen-Akten (Issue #474)
 *
 * Zeigt alle Stati des Mietwagen-Lebenszyklus als horizontale Schritte.
 * Der aktuelle Schritt ist hervorgehoben; abgeschlossene Schritte sind grün markiert.
 * Ein "Weiterschalten"-Button ruft /api/mietwagen-workflow auf, sobald der nächste
 * Schritt möglich und ein Klick erfolgt.
 *
 * Issue #615: CTA-Buttons pro Workflow-Schritt (Übergabe, Rücknahme, Abrechnung)
 */
import { useState } from "react";
import { Check, ChevronRight, Loader2, ArrowRightLeft, RotateCcw, Receipt } from "lucide-react";
import { toast } from "sonner";
import {
  MIETWAGEN_STATUS_VALUES,
  MIETWAGEN_STATUS_LABELS,
  isMietwagenStatus,
  type MietwagenStatus,
} from "@/lib/mietwagenStatus";

interface MietwagenWorkflowStepperProps {
  akteId: string;
  status: MietwagenStatus | string | null | undefined;
  onStatusChanged: () => void;
  accessToken?: string | null;
  /** Issue #615: CTA-Callbacks pro Workflow-Schritt */
  onUebergabe?: () => void;
  onRueckgabe?: () => void;
  onAbrechnen?: () => void;
}

async function setzeStatus(
  akteId: string,
  neuerStatus: MietwagenStatus,
  accessToken?: string | null,
): Promise<void> {
  const res = await fetch("/api/mietwagen-workflow", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({
      action: "setzeStatus",
      mietwagen_akte_id: akteId,
      neuer_status: neuerStatus,
    }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error ?? `HTTP ${res.status}`);
  }
}

export default function MietwagenWorkflowStepper({
  akteId,
  status,
  onStatusChanged,
  accessToken,
  onUebergabe,
  onRueckgabe,
  onAbrechnen,
}: MietwagenWorkflowStepperProps) {
  const [advancing, setAdvancing] = useState(false);

  const currentStatus: MietwagenStatus = isMietwagenStatus(status)
    ? status
    : "vertrag_offen";

  const currentIndex = MIETWAGEN_STATUS_VALUES.indexOf(currentStatus);
  const nextStatus: MietwagenStatus | null =
    currentIndex < MIETWAGEN_STATUS_VALUES.length - 1
      ? MIETWAGEN_STATUS_VALUES[currentIndex + 1]
      : null;

  const handleAdvance = async () => {
    if (!nextStatus || advancing) return;
    setAdvancing(true);
    try {
      await setzeStatus(akteId, nextStatus, accessToken);
      toast.success(`Status aktualisiert: ${MIETWAGEN_STATUS_LABELS[nextStatus]}`);
      onStatusChanged();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Fehler";
      toast.error("Status konnte nicht geändert werden", { description: msg });
    } finally {
      setAdvancing(false);
    }
  };

  return (
    <div
      data-testid="mietwagen-workflow-stepper"
      className="bg-white rounded-xl border border-gray-200 p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-gray-900">Vorgangs-Workflow</h3>
        <div className="flex items-center gap-2">
          {/* CTA-Buttons pro Schritt (Issue #615) */}
          {currentStatus === "vertrag_offen" && onUebergabe && (
            <button
              type="button"
              data-testid="cta-uebergabe"
              onClick={onUebergabe}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-[11px] font-semibold hover:bg-emerald-700 transition-colors"
            >
              <ArrowRightLeft className="w-3 h-3" />
              Jetzt übergeben
            </button>
          )}
          {currentStatus === "in_miete" && onRueckgabe && (
            <button
              type="button"
              data-testid="cta-rueckgabe"
              onClick={onRueckgabe}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-[11px] font-semibold hover:bg-amber-700 transition-colors"
            >
              <RotateCcw className="w-3 h-3" />
              Jetzt zurücknehmen
            </button>
          )}
          {currentStatus === "zurueckgegeben" && onAbrechnen && (
            <button
              type="button"
              data-testid="cta-abrechnen"
              onClick={onAbrechnen}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-[11px] font-semibold hover:bg-purple-700 transition-colors"
            >
              <Receipt className="w-3 h-3" />
              Jetzt abrechnen
            </button>
          )}
          {nextStatus && (
            <button
              type="button"
              data-testid="workflow-advance-button"
              onClick={handleAdvance}
              disabled={advancing}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-[11px] font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {advancing ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
              Weiterschalten zu: {MIETWAGEN_STATUS_LABELS[nextStatus]}
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-0 overflow-x-auto pb-1">
        {MIETWAGEN_STATUS_VALUES.map((s, idx) => {
          const isDone = idx < currentIndex;
          const isCurrent = idx === currentIndex;
          const isPending = idx > currentIndex;

          return (
            <div key={s} className="flex items-center flex-shrink-0">
              {/* Step dot */}
              <div className="flex flex-col items-center gap-1">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                    isDone
                      ? "bg-emerald-500 text-white"
                      : isCurrent
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {isDone ? <Check className="w-3 h-3" /> : idx + 1}
                </div>
                <span
                  className={`text-[9px] sm:text-[10px] font-medium text-center leading-tight max-w-[56px] sm:max-w-[68px] ${
                    isDone
                      ? "text-emerald-600"
                      : isCurrent
                        ? "text-blue-700"
                        : isPending
                          ? "text-gray-400"
                          : "text-gray-600"
                  }`}
                >
                  {MIETWAGEN_STATUS_LABELS[s]}
                </span>
              </div>

              {/* Connector */}
              {idx < MIETWAGEN_STATUS_VALUES.length - 1 && (
                <div
                  className={`h-0.5 w-6 sm:w-10 mx-1 flex-shrink-0 rounded ${
                    idx < currentIndex ? "bg-emerald-400" : "bg-gray-200"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
